"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CONTACT, NAV } from "@/lib/data";
import { Logo } from "./Logo";
import { Magnetic } from "./Magnetic";

export function Header() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 right-0 left-0 z-[60] flex items-center justify-between px-5 py-4 transition-all duration-500 md:px-8 ${
          scrolled ? "bg-ink/80 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <Link href="/" className="font-display flex items-baseline gap-2 text-2xl tracking-[0.18em]" data-cursor="hover">
          STO
          <span className="hidden font-sans text-[10px] tracking-[0.2em] text-brass sm:inline">
            Ostwald
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`text-xs tracking-[0.16em] uppercase transition-opacity ${
                pathname === item.href ? "text-brass opacity-100" : "opacity-55 hover:opacity-100"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Magnetic className="hidden md:block">
            <Link
              href="/contact"
              className="inline-flex items-center rounded-sm border border-brass/40 bg-brass px-5 py-2 text-xs tracking-[0.16em] text-ink uppercase"
            >
              Contact
            </Link>
          </Magnetic>
          <button
            type="button"
            className="relative z-[70] flex h-11 w-11 flex-col items-center justify-center gap-1.5 lg:hidden"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            onClick={() => setOpen((v) => !v)}
          >
            <span className={`h-px w-6 bg-paper transition ${open ? "translate-y-[4px] rotate-45" : ""}`} />
            <span className={`h-px w-6 bg-paper transition ${open ? "-translate-y-[4px] -rotate-45" : ""}`} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex flex-col justify-end bg-ink px-6 pb-10 lg:hidden"
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
          >
            <nav className="flex flex-col gap-1">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.href}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.12 + i * 0.06 }}
                >
                  <Link href={item.href} className="font-display block text-5xl">
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <a href={CONTACT.phoneHref} className="font-display mt-6 text-2xl text-brass">
                {CONTACT.phone}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
